import { Inject, Injectable } from '@angular/core';
import { combineLatest, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, map, tap, withLatestFrom } from 'rxjs/operators';
import { SUPPORTED_LANGUAGES, SupportedLanguage } from '@shared/localization';
import type { PipelineStepDraftDto } from '@protocols/use-cases';
import {
  RetrievePipelineDetailErrorCode,
  RetrievePipelineDetailException,
  SavePipelineErrorCode,
  SavePipelineException,
} from '@protocols/use-cases';

import { RolesStore } from '@telespot/authentication/ui';

import { LabelConsolidationFailureEffects, LabelFailureEvent } from '../../../effects/label-consolidation-failure.effects';
import { LabelEffects } from '../../../effects/label.effects';
import { PipelineEditorEffects } from '../../../effects/pipeline-editor.effects';
import { PipelineStepEditorEffects } from '../../../effects/pipeline-step-editor.effects';
import { PipelineEditorState, PipelineStepListItem } from '../../../state/pipeline-editor.state';
import { PipelineEnvironment } from '@telespot/domain';

/**
 * Shape of the unified error stream the facade emits to the smart component.
 * The `key` is an i18n key; `params` is optional placeholder substitution
 * passed straight to `| translate: error.params` in the template. File-local
 * because only this facade and its own template need to know it.
 */
type ErrorMessage = { key: string; params?: Record<string, unknown> };

@Injectable()
export class PipelineEditorFacade {
  constructor(
    private readonly editorEffects: PipelineEditorEffects,
    private readonly stepEditorEffects: PipelineStepEditorEffects,
    private readonly labelEffects: LabelEffects,
    private readonly labelFailureEffects: LabelConsolidationFailureEffects,
    private readonly state: PipelineEditorState,
    private readonly rolesStore: RolesStore,
    @Inject(SUPPORTED_LANGUAGES) readonly supportedLanguages: SupportedLanguage[]
  ) {}

  /** Triggered by the component on init — I/O is driven by lifecycle, not DI. */
  public readonly loadRoles = this.state.effect<void>((trigger$) =>
    trigger$.pipe(tap(() => this.rolesStore.fetchRoles()))
  );

  // ============================================================================
  // Selectors — passthrough
  // ============================================================================

  public readonly id$ = this.state.id$;
  public readonly name$ = this.state.name$;
  public readonly description$ = this.state.description$;
  public readonly env$ = this.state.env$;
  public readonly steps$ = this.state.steps$;
  public readonly loading$ = this.state.loading$;
  public readonly canChangeEnv$ = this.state.canChangeEnv$;
  public readonly hasSteps$ = this.state.hasSteps$;
  public readonly displayNameValid$ = this.state.displayNameValid$;

  // ============================================================================
  // Selectors — UI-shaped derivations (state stays raw, facade composes)
  // ============================================================================

  /**
   * In-flight signal for any save activity reaching the editor — the editor
   * state's own `saving` (pipeline save command) OR the label state's `saving`
   * (label consolidation phase that precedes the pipeline save). Without the
   * label-side, the Save button is unguarded during consolidation.
   */
  public readonly saving$: Observable<boolean> = combineLatest([this.state.saving$, this.labelEffects.saving$]).pipe(
    map(([editorSaving, labelSaving]) => editorSaving || labelSaving),
    distinctUntilChanged()
  );

  /**
   * True while either the initial load or any save phase is in flight.
   */
  public readonly busy$: Observable<boolean> = combineLatest([this.state.loading$, this.saving$]).pipe(
    map(([loading, saving]) => loading || saving)
  );

  /**
   * Whether cancelling now would discard user work — metadata, step edits,
   * reorders or deletions. Live signature comes from the shared effects (same
   * stream `captureBaseline` snapshots). False until a seed completes
   * (`baselineSignature` still `null`), so a failed load never warns.
   */
  public readonly hasChanges$: Observable<boolean> = this.state.select(
    this.editorEffects.currentSignature$,
    this.state.baselineSignature$,
    (current, baseline) => baseline !== null && current !== baseline
  );

  /**
   * Authorization gate — only admins may modify pipelines. Sourced from the
   * shared `RolesStore` (the same instance the step-editor facade reads, since
   * `rolesStoreProviders()` is pulled in once via `pipelineStepEditorProviders`).
   * `startWith(false)` makes the editor fail closed until roles resolve.
   */
  public readonly isAdmin$ = this.rolesStore.isAdmin$;

  /**
   * Drives the step-management list's `disabled` input: the list is locked
   * while any save is in flight OR whenever the user is not an admin (read-only).
   */
  public readonly stepListDisabled$: Observable<boolean> = combineLatest([this.isAdmin$, this.saving$]).pipe(
    map(([isAdmin, saving]) => !isAdmin || saving),
    distinctUntilChanged()
  );

  /**
   * Per-control editability for the metadata form. Non-admins get a fully
   * read-only form; for admins the env control additionally follows
   * `canChangeEnv$` (locked once steps exist). Composed here so the component
   * binds one stream instead of combining the sources itself.
   */
  public readonly formEditability$: Observable<{ name: boolean; description: boolean; env: boolean }> = combineLatest([
    this.isAdmin$,
    this.canChangeEnv$,
  ]).pipe(
    map(([isAdmin, canChangeEnv]) => ({
      name: isAdmin,
      description: isAdmin,
      env: isAdmin && canChangeEnv,
    })),
    distinctUntilChanged((a, b) => a.name === b.name && a.description === b.description && a.env === b.env)
  );

  /**
   * Unified error stream for the page banner.
   *
   * Pipeline-save and editor-state errors only — label and label-config errors
   * surface as snackbars via `labelSaveFailed$` / `labelConfigSaveFailed$`
   * below. Effects never write i18n keys; they push the typed exception
   * through state and the facade dispatches into its own namespace below.
   */
  public readonly error$: Observable<ErrorMessage | null> = combineLatest([
    this.state.error$,
    this.state.loadException$,
    this.state.saveException$,
  ]).pipe(
    map(([editorKey, loadEx, saveEx]) => {
      if (editorKey) return { key: editorKey };
      if (loadEx) return this.loadErrorToMessage(loadEx);
      if (saveEx) return this.saveErrorToMessage(saveEx);

      return null;
    }),
    distinctUntilChanged((a, b) => a?.key === b?.key && JSON.stringify(a?.params) === JSON.stringify(b?.params))
  );

  private loadErrorToMessage(error: Error): ErrorMessage {
    if (error instanceof RetrievePipelineDetailException) {
      switch (error.code) {
        case RetrievePipelineDetailErrorCode.PIPELINE_NOT_FOUND:
          return { key: 'pipeline_editor.errors.pipeline_not_found' };
        case RetrievePipelineDetailErrorCode.REPOSITORY_ERROR:
          return { key: 'pipeline_editor.errors.repository_error' };
      }
    }
    return { key: 'pipeline_editor.errors.load_failed' };
  }

  private saveErrorToMessage(error: Error): ErrorMessage {
    if (error instanceof SavePipelineException) {
      switch (error.code) {
        case SavePipelineErrorCode.MISSING_NAME_EN:
          return { key: 'pipeline_editor.errors.missing_name_en' };
        case SavePipelineErrorCode.MISSING_PERSISTED_ID:
          return { key: 'pipeline_editor.errors.missing_persisted_id' };
      }
    }
    return { key: 'pipeline_editor.errors.save_failed' };
  }

  /**
   * Each failing label-save emits a `{ name, error }` pair the smart component
   * dispatches into a snackbar (shared `LabelConsolidationFailureEffects`
   * derivation — display name already resolved in the current app language).
   */
  public readonly labelSaveFailed$: Observable<LabelFailureEvent> = this.labelFailureEffects.labelSaveFailed$;

  /** Per-row label-config tail failures. See `labelSaveFailed$`. */
  public readonly labelConfigSaveFailed$: Observable<LabelFailureEvent> =
    this.labelFailureEffects.labelConfigSaveFailed$;

  /**
   * UI-shaped projection of `steps$` for the step-management list.
   * `uuid` is the array index — the editor's drafts are positional, and the
   * list item identity follows that positional encoding.
   */
  public readonly stepListItems$: Observable<PipelineStepListItem[]> = this.state.steps$.pipe(
    map((steps) => steps.map((step, i) => ({ uuid: String(i), order: step.order, task: step.task })))
  );

  // ============================================================================
  // Effects + updaters — passthrough
  // ============================================================================

  /**
   * Cancel is a pure handoff: emit the one-shot `cancelled` outcome on the
   * shared effects. The consumer (route page facade, dialog wrapper) decides
   * what to do — navigate back, close the dialog, discard the draft.
   */
  public readonly cancel = this.state.effect<void>((trigger$) =>
    trigger$.pipe(tap(() => this.editorEffects.confirm({ kind: 'cancelled' })))
  );

  /**
   * Save is a pure handoff too: it does **not** persist. It emits the one-shot
   * `saved` outcome on the shared effects; the draft is available in the shared
   * state as `draft$`. The consumer owns persistence — the standalone route page
   * runs the protocol cascade on `saved`, while the method-type dialog consumer
   * reads the draft and stages it for the deferred method-type save.
   */
  public readonly save = this.state.effect<void>((trigger$) =>
    trigger$.pipe(tap(() => this.editorEffects.confirm({ kind: 'saved' })))
  );

  /**
   * Typed form `valueChanges` emit `Partial<{ [lang: string]: string }>` —
   * disabled controls drop out. The state expects a complete record (one
   * entry per supported language), so the facade fills the missing keys
   * with empty strings before mutating state.
   */
  public readonly setName = this.state.effect((name$: Observable<Partial<{ [lang: string]: string }>>) =>
    name$.pipe(tap((partial: Partial<{ [lang: string]: string }>) => this.state.setName(this.toFullLangMap(partial))))
  );
  public readonly setDescription = this.state.effect((description$: Observable<Partial<{ [lang: string]: string }>>) =>
    description$.pipe(
      tap((partial: Partial<{ [lang: string]: string }>) => this.state.setDescription(this.toFullLangMap(partial)))
    )
  );

  public readonly setEnv = this.state.setEnv;
  public readonly removeStep = this.state.removeStep;
  public readonly reorderSteps = this.editorEffects.reorderSteps;

  private toFullLangMap(partial: Partial<{ [lang: string]: string }>): { [lang: string]: string } {
    return Object.fromEntries(this.supportedLanguages.map((lang) => [lang.value, partial[lang.value] ?? '']));
  }

  // ============================================================================
  // Step-dialog choreography
  // ============================================================================

  private readonly _openStepDialog$ = new Subject<void>();

  /**
   * One-shot signal emitted when a step-editor dialog should open.
   * The consuming component subscribes and opens the dialog with its own
   * injector so the shared `PipelineStepEditorState` instance reaches the
   * dialog's injector chain.
   */
  public readonly openStepDialog$: Observable<void> = this._openStepDialog$.asObservable();

  /**
   * Seeds the step-editor state with a blank context for the next slot and
   * marks the editor as "adding a new step" (editingIndex = -1).
   */
  public readonly beginAddStep = this.state.effect<void>((trigger$) =>
    trigger$.pipe(
      withLatestFrom(this.state.steps$, this.state.env$, this.state.id$, this.state.editingIndex$),
      tap(
        ([, steps, env, pipelineId, editingIndex]: [
          void,
          PipelineStepDraftDto[],
          PipelineEnvironment,
          string | null,
          number | null
        ]) => {
          // Ignore re-entry while a session is open; it would overwrite the
          // pending `editingIndex` before its `completeStepEdit` resolves
          if (editingIndex !== null) return;

          this.state.setEditingIndex(-1);
          this.stepEditorEffects.initialize({
            context: {
              pipelineId: pipelineId ?? '',
              environment: env,
              order: steps.length,
              otherStepsTasks: steps.map((s) => s.task),
            },
          });
          this._openStepDialog$.next();
        }
      )
    )
  );

  /**
   * Opens the step editor for an existing draft. Persisted steps (with an id)
   * trigger `loadStep` so the full server-side detail (with resolved label
   * names) is loaded; purely in-memory drafts are seeded directly.
   */
  public readonly beginEditStep = this.state.effect((index$: Observable<number>) =>
    index$.pipe(
      withLatestFrom(
        this.state.steps$,
        this.state.env$,
        this.state.id$,
        this.state.editingIndex$,
        this.state.editedStepIds$
      ),
      tap(
        ([index, steps, env, pipelineId, editingIndex, editedStepIds]: [
          number,
          PipelineStepDraftDto[],
          PipelineEnvironment,
          string | null,
          number | null,
          string[]
        ]) => {
          // Ignore re-entry while a session is open
          if (editingIndex !== null) return;

          const step = steps[index];
          if (!step) return;
          this.state.setEditingIndex(index);
          const context = {
            pipelineId: pipelineId ?? '',
            environment: env,
            order: step.order,
            otherStepsTasks: steps.filter((_, i) => i !== index).map((s) => s.task),
          };

          // Re-fetch a pristine persisted structured step, unless
          // it has been edited locally, then the staged draft is authoritative
          // seeding from the DB would discard those edits
          if (step.id && step.mode === 'structured' && !editedStepIds.includes(step.id)) {
            this.stepEditorEffects.loadStep({ stepId: step.id, context });
          } else {
            this.stepEditorEffects.seedFromDraft({ context, step });
          }

          this._openStepDialog$.next();
        }
      )
    )
  );

  /**
   * Applies the dialog's outcome: appends when `editingIndex === -1`, replaces
   * when it points at an existing slot, and resets it in all cases.
   */
  public readonly completeStepEdit = this.state.effect((result$: Observable<PipelineStepDraftDto | null | undefined>) =>
    result$.pipe(
      withLatestFrom(this.state.editingIndex$),
      tap(([draft, editingIndex]: [PipelineStepDraftDto | null | undefined, number | null]) => {
        if (!draft) {
          this.state.setEditingIndex(null);
          return;
        }

        if (editingIndex === null || editingIndex < 0) {
          this.state.appendStep(draft);
        } else {
          this.state.replaceStep({ index: editingIndex, step: draft });
        }
        this.state.setEditingIndex(null);
      })
    )
  );
}
